import { useEffect, useMemo, useState } from 'react';
import {
  getContact, sendOutreach, setContactStatus, updateContact, expandOutreach, logSms,
  type OutreachContact, type OutreachTemplate, type OutreachStatus,
} from '@/lib/api/outreach';
import { sendSmsViaBridge, getSmsBridgeUrl, setSmsBridgeUrl } from '@/lib/smsBridge';
import { useToast } from '@/lib/hooks/useToast';

const STATUSES: OutreachStatus[] = ['new', 'drafted', 'sent', 'replied', 'declined', 'bounced', 'customer'];
const statusColor: Record<string, string> = {
  new: 'bg-gray-100 text-gray-600', drafted: 'bg-blue-100 text-blue-700', sent: 'bg-amber-100 text-amber-700',
  replied: 'bg-green-100 text-green-700', declined: 'bg-red-100 text-red-700', bounced: 'bg-red-100 text-red-700',
  customer: 'bg-emerald-100 text-emerald-700',
};

// Apply the recipient greeting to "[GC Name]"-style tokens (but not [Your Name]).
const applyGreeting = (text: string, greeting: string) =>
  greeting ? text.replace(/\[(?!Your Name\])[^\]]*Name\]/g, greeting) : text;

interface Props {
  contactId: number;
  snippets: Record<string, string>;
  templates: OutreachTemplate[];
  senderName: string;
  replyTo: string;
  onClose: () => void;
  onChanged: () => void;
}

export function ComposeDrawer({ contactId, snippets, templates, senderName, replyTo, onClose, onChanged }: Props) {
  const { toast } = useToast();
  const [c, setC] = useState<OutreachContact | null>(null);
  const [greeting, setGreeting] = useState('there');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  // SMS channel (via local ADB bridge)
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsCfg, setSmsCfg] = useState(false);
  const [smsBridgeUrl, setSmsBridgeUrlState] = useState(getSmsBridgeUrl());

  useEffect(() => {
    getContact(contactId).then(data => {
      setC(data);
      setGreeting(data.draft_greeting || 'there');
      setSubject(data.draft_subject || '');
      setBody(data.draft_body || '');
      setSmsPhone((data.phone || '').replace(/[^\d+]/g, ''));
    }).catch(() => toast.error('Failed to load contact'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const previewSubject = useMemo(() => expandOutreach(subject, snippets, senderName, greeting), [subject, snippets, senderName, greeting]);
  const previewBody = useMemo(() => expandOutreach(body, snippets, senderName, greeting), [body, snippets, senderName, greeting]);

  const loadTemplate = (id: string) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setSubject(applyGreeting(t.subject || '', greeting));
    setBody(applyGreeting(t.body || '', greeting));
  };

  const saveDraft = async () => {
    if (!c) return;
    await updateContact(c.id, { draft_greeting: greeting, draft_subject: subject, draft_body: body, status: c.status === 'new' ? 'drafted' : undefined });
    toast.success('Draft saved');
    onChanged();
  };

  const send = async () => {
    if (!c) return;
    if (!c.primary_email) { toast.error('No valid email on this contact'); return; }
    if (!window.confirm(`Send this email to ${c.primary_email} as OchrePoint (info@ochrepoint.ca)?`)) return;
    setSending(true);
    try {
      await sendOutreach(c.id, { subject, body, senderName, replyTo });
      toast.success(`Sent to ${c.primary_email}`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const sendSms = async () => {
    if (!c) return;
    const text = expandOutreach(smsText, snippets, senderName, greeting).trim();
    if (!smsPhone) { toast.error('No phone number'); return; }
    if (!text) { toast.error('Empty message'); return; }
    if (!window.confirm(`Text ${smsPhone} from your phone via the SMS bridge?`)) return;
    setSmsSending(true);
    try {
      await sendSmsViaBridge(smsPhone, text);
      await logSms(c.id, { to: smsPhone, body: text }).catch(() => {});
      toast.success(`SMS sent to ${smsPhone}`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error((e as Error).message || 'SMS failed');
    } finally {
      setSmsSending(false);
    }
  };

  const saveSmsBridge = () => { setSmsBridgeUrl(smsBridgeUrl); setSmsCfg(false); toast.success('SMS bridge saved'); };

  const copy = async (text: string, label: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${label}`); } catch { toast.error('Copy failed'); }
  };

  const changeStatus = async (s: OutreachStatus) => {
    if (!c) return;
    setC({ ...c, status: s });
    await setContactStatus(c.id, s).catch(() => {});
    onChanged();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full shadow-xl flex flex-col">
        {!c ? (
          <div className="p-6 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />)}</div>
        ) : (
          <>
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 truncate">{c.name}</h3>
                <p className="text-xs text-gray-500">{c.category} · {c.location || '—'}</p>
                <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                  {c.primary_email
                    ? <a href={`mailto:${c.primary_email}`} className="text-primary hover:underline">{c.primary_email}</a>
                    : <span className="text-red-500">no email</span>}
                  {c.phone && <span className="text-gray-400">· {c.phone}</span>}
                  {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-primary">· website</a>}
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {c.suppressed && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-sm">block</span>
                  This contact has unsubscribed / is suppressed. Email sending is disabled.
                </div>
              )}
              {c.specialty && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2.5 leading-relaxed">{c.specialty}</p>}

              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
                {(['email', 'sms'] as const).map(ch => (
                  <button key={ch} onClick={() => setChannel(ch)}
                    className={`px-3 h-7 rounded-md text-xs font-semibold ${channel === ch ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
                    {ch === 'email' ? 'Email' : 'SMS'}
                  </button>
                ))}
              </div>

              {channel === 'email' && (<>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Status</span>
                  <select value={c.status} onChange={e => changeStatus(e.target.value as OutreachStatus)}
                    className={`h-8 px-2 rounded-lg border border-gray-300 text-xs font-medium ${statusColor[c.status] || ''}`}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Greeting</span>
                  <input value={greeting} onChange={e => setGreeting(e.target.value)} placeholder="there / first name"
                    className="h-8 px-2 rounded-lg border border-gray-300 text-sm w-32" />
                </label>
                <label className="flex flex-col gap-1 flex-1 min-w-[10rem]">
                  <span className="text-xs text-gray-500">Load template</span>
                  <select onChange={e => { if (e.target.value) loadTemplate(e.target.value); e.target.value = ''; }}
                    className="h-8 px-2 rounded-lg border border-gray-300 text-sm">
                    <option value="">Choose a template…</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.title}{t.category ? ` (${t.category})` : ''}</option>)}
                  </select>
                </label>
                <button onClick={() => setPreview(p => !p)}
                  className={`h-8 px-3 rounded-lg text-xs font-semibold border ${preview ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white border-gray-300 text-gray-600'}`}>
                  {preview ? 'Editing' : 'Preview'}
                </button>
              </div>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 flex justify-between"><span>Subject</span>
                  <button onClick={() => copy(previewSubject, 'subject')} className="text-primary hover:underline">copy</button></span>
                {preview
                  ? <div className="min-h-[2.25rem] px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm">{previewSubject}</div>
                  : <input value={subject} onChange={e => setSubject(e.target.value)} className="h-9 px-3 rounded-lg border border-gray-300 text-sm" />}
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 flex justify-between"><span>Body</span>
                  <button onClick={() => copy(previewBody, 'body')} className="text-primary hover:underline">copy</button></span>
                {preview
                  ? <div className="whitespace-pre-wrap px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm leading-relaxed min-h-[16rem]">{previewBody}</div>
                  : <textarea value={body} onChange={e => setBody(e.target.value)} rows={16}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm leading-relaxed font-sans" />}
              </label>
              </>)}

              {channel === 'sms' && (
                <div className="space-y-3">
                  <div className="flex items-end gap-2">
                    <label className="flex flex-col gap-1 flex-1">
                      <span className="text-xs text-gray-500">Text to</span>
                      <input value={smsPhone} onChange={e => setSmsPhone(e.target.value)} placeholder="+1250…"
                        className="h-9 px-3 rounded-lg border border-gray-300 text-sm font-mono" />
                    </label>
                    <button onClick={() => setSmsCfg(o => !o)} className="h-9 px-3 rounded-lg border border-gray-300 text-xs text-gray-500 hover:bg-gray-50">⚙ Bridge</button>
                  </div>
                  {smsCfg && (
                    <div className="flex items-end gap-2 bg-gray-50 rounded-lg p-2">
                      <label className="flex flex-col gap-1 flex-1">
                        <span className="text-xs text-gray-500">SMS bridge URL (on the PC with the phone)</span>
                        <input value={smsBridgeUrl} onChange={e => setSmsBridgeUrlState(e.target.value)} placeholder="http://127.0.0.1:8791"
                          className="h-8 px-2 rounded-lg border border-gray-300 text-sm font-mono" />
                      </label>
                      <button onClick={saveSmsBridge} className="h-8 px-3 rounded-lg bg-primary text-white text-xs font-semibold">Save</button>
                    </div>
                  )}
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 flex justify-between">
                      <span>Message</span><span className="text-gray-400">{expandOutreach(smsText, snippets, senderName, greeting).length} chars</span>
                    </span>
                    <textarea value={smsText} onChange={e => setSmsText(e.target.value)} rows={5}
                      placeholder="Short intro text… (use the AI SMS templates for ideas)"
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm leading-relaxed" />
                  </label>
                  <p className="text-xs text-gray-400">Sends from your phone via the local ADB bridge (see tools/sms-bridge).</p>
                </div>
              )}

              {c.messages.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-1">History</p>
                  {c.messages.map(m => (
                    <div key={m.id} className="text-xs text-gray-500 flex justify-between border-b border-gray-50 py-1">
                      <span className="truncate">{m.channel} · {m.subject}</span>
                      <span className="shrink-0 ml-2">{new Date(m.created_at).toLocaleDateString('en-CA')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100">
              {channel === 'email' ? (
                <>
                  <button onClick={saveDraft} className="h-9 px-4 rounded-lg text-sm text-gray-700 border border-gray-300 hover:bg-gray-50">Save draft</button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => copy(`${previewSubject}\n\n${previewBody}`, 'full email')} className="h-9 px-3 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Copy all</button>
                    <button onClick={send} disabled={sending || !c.primary_email || c.suppressed}
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
                      <span className={`material-symbols-outlined text-base ${sending ? 'animate-spin' : ''}`}>{sending ? 'progress_activity' : 'send'}</span>
                      {sending ? 'Sending…' : 'Send as OchrePoint'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button onClick={() => copy(expandOutreach(smsText, snippets, senderName, greeting), 'message')} className="h-9 px-3 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Copy</button>
                  <button onClick={sendSms} disabled={smsSending || !smsPhone}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
                    <span className={`material-symbols-outlined text-base ${smsSending ? 'animate-spin' : ''}`}>{smsSending ? 'progress_activity' : 'sms'}</span>
                    {smsSending ? 'Sending…' : 'Send SMS'}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
